/**
 * Pure logic for the calibre-side "Reapply to matching references" tool.
 *
 * The tool is the brand-side Calibre Enrichment run the other way round: start
 * from ONE calibre and find its references in every brand. It must behave
 * exactly as that tool does, so nothing here re-implements a matching or
 * writing rule. `findReferencesForCalibre` (a read-only callable) casts a wide
 * net; this module hands each brand's candidates to the enricher's own
 * {@link buildBrandScopedIndex} + {@link matchRefForBrand} and keeps a reference
 * only when they assign it to this calibre. What gets written is the
 * enricher's own {@link buildRefUpdates}.
 */

import { isCatchAllCalibre, isDeprecatedCalibre, buildCalibreIdIndex } from '../../services/calibreResolution';
import type { CalibreReferenceCandidate, CustomCalibreEntry } from '../../services/firebase';
import type { WatchBrand, WatchReference } from '../../types';
import { deriveIsVariant } from '../../utils/references';
import { buildBrandScopedIndex, matchRefForBrand } from './BulkCalibreEnricher';
import { type CalibreDualWriteFlags } from './calibreBulkUpdate';
import { buildRefUpdates } from './parentCalibrePush';

/** How the brand tool matched a reference: by `calibreId`, canonical name, or alias. */
export type ReapplyMatchSource = 'link' | 'name' | 'alias';

/** Which match sources the curator has ticked. */
export type ReapplySources = Record<ReapplyMatchSource, boolean>;

export const ALL_REAPPLY_SOURCES: ReapplySources = { link: true, name: true, alias: true };

export interface ReapplyMatchedRef {
  ref: WatchReference;
  source: ReapplyMatchSource;
  /** The calibre text the reference showed when it was matched. */
  storedCalibre: string;
  /** A variant (has a parent reference). Left out unless variants are included. */
  isVariant: boolean;
}

export interface ReapplyBrandMatches {
  matched: ReapplyMatchedRef[];
  /** Candidates the brand rules gave to another calibre, or to none. */
  rejected: number;
}

/**
 * Why the tool is unavailable for a calibre, or null when it can run. The brand
 * tool never matches a deprecated calibre by name and never matches a catch-all
 * by its universal names, so reapplying either would be a different operation.
 */
export function reapplyBlockedReason(
  entry: Pick<CustomCalibreEntry, 'deprecated' | 'data'> | null | undefined,
): string | null {
  if (!entry) return 'Save the calibre first.';
  if (isDeprecatedCalibre(entry as CustomCalibreEntry)) {
    return 'This calibre is deprecated — references are matched to the calibre it redirects to.';
  }
  if (isCatchAllCalibre(entry as CustomCalibreEntry)) {
    return 'Catch-all calibres are placeholders and are never matched by name.';
  }
  return null;
}

/** Candidate reference ids per brand, de-duplicated (a ref can be found twice). */
export function groupCandidateIdsByBrand(
  candidates: readonly Pick<CalibreReferenceCandidate, 'brandId' | 'refId'>[],
): Map<string, string[]> {
  const sets = new Map<string, Set<string>>();
  for (const c of candidates) {
    if (!c.brandId || !c.refId) continue;
    let set = sets.get(c.brandId);
    if (!set) sets.set(c.brandId, (set = new Set()));
    set.add(c.refId);
  }
  return new Map(Array.from(sets, ([brandId, ids]) => [brandId, Array.from(ids)]));
}

/**
 * Of one brand's candidate references, the ones the brand tool would put in
 * THIS calibre's group — labelled link / name / alias exactly as it would.
 *
 * A candidate is rejected when the brand tool would resolve it elsewhere: it is
 * linked to a different live calibre, another calibre's canonical name outranks
 * this calibre's alias on the same text, or the alias is not on offer to this
 * brand.
 */
export function verifyBrandCandidates(
  calibreId: string,
  brand: WatchBrand,
  refs: readonly WatchReference[],
  allCalibres: CustomCalibreEntry[],
): ReapplyBrandMatches {
  const index = buildBrandScopedIndex(allCalibres, brand);
  const calibresById = buildCalibreIdIndex(allCalibres);
  const matched: ReapplyMatchedRef[] = [];
  let rejected = 0;
  for (const ref of refs) {
    const { raw, hit } = matchRefForBrand(ref, calibresById, index);
    if (hit && hit.entry.id === calibreId) {
      matched.push({
        ref,
        source: hit.matchType,
        storedCalibre: raw,
        isVariant: deriveIsVariant(ref.parentReferenceId),
      });
    } else {
      rejected += 1;
    }
  }
  matched.sort((a, b) => (a.ref.reference || '').localeCompare(b.ref.reference || ''));
  return { matched, rejected };
}

/**
 * The matched references Apply will act on: match source ticked, and — unless
 * variants are included — parents and standalone references only.
 */
export function selectReapplyRefs(
  matched: readonly ReapplyMatchedRef[],
  sources: ReapplySources,
  includeVariants: boolean,
): ReapplyMatchedRef[] {
  return matched.filter((m) => sources[m.source] && (includeVariants || !m.isVariant));
}

export interface ReapplyBrandSummary {
  link: number;
  name: number;
  alias: number;
  /** Matched variants, whether or not they are included. */
  variants: number;
  /** References in the ticked sources (and variant scope) — what Apply will look at. */
  selected: number;
  /** Of those, how many would actually be written. */
  wouldChange: number;
  /** Content fields across those writes (bookkeeping fields excluded). */
  fields: number;
  /** The same total by field path: how many references each field is written on. */
  fieldCounts: Record<string, number>;
}

/**
 * One brand's row: counts per match source (ticked or not, within the variant
 * scope), how many variants matched, and what applying the selection would
 * write — computed with the brand tool's own payload builder, so "would change"
 * means the same thing in both tools.
 */
export function summariseBrandMatches(
  matched: readonly ReapplyMatchedRef[],
  entry: Pick<CustomCalibreEntry, 'id' | 'name' | 'data'>,
  flags: CalibreDualWriteFlags,
  sources: ReapplySources,
  includeVariants: boolean,
): ReapplyBrandSummary {
  const summary: ReapplyBrandSummary = {
    link: 0, name: 0, alias: 0, variants: 0, selected: 0, wouldChange: 0, fields: 0, fieldCounts: {},
  };
  for (const m of matched) {
    if (m.isVariant) {
      summary.variants += 1;
      if (!includeVariants) continue;
    }
    summary[m.source] += 1;
    if (!sources[m.source]) continue;
    summary.selected += 1;
    const keys = Object.keys(buildRefUpdates(m.ref, entry, flags));
    if (keys.length > 0) {
      summary.wouldChange += 1;
      summary.fields += keys.length;
      for (const key of keys) summary.fieldCounts[key] = (summary.fieldCounts[key] ?? 0) + 1;
    }
  }
  return summary;
}

/** Column-wise total of brand summaries. */
export function totalReapplySummary(rows: readonly ReapplyBrandSummary[]): ReapplyBrandSummary {
  const total: ReapplyBrandSummary = {
    link: 0, name: 0, alias: 0, variants: 0, selected: 0, wouldChange: 0, fields: 0, fieldCounts: {},
  };
  for (const r of rows) {
    for (const [key, n] of Object.entries(r.fieldCounts)) {
      total.fieldCounts[key] = (total.fieldCounts[key] ?? 0) + n;
    }
    total.variants += r.variants;
    total.link += r.link;
    total.name += r.name;
    total.alias += r.alias;
    total.selected += r.selected;
    total.wouldChange += r.wouldChange;
    total.fields += r.fields;
  }
  return total;
}

/** A field breakdown as rows, most-written first, ties by field path. */
export function fieldBreakdownRows(
  fieldCounts: Readonly<Record<string, number>>,
): { field: string; count: number }[] {
  return Object.entries(fieldCounts)
    .map(([field, count]) => ({ field, count }))
    .sort((a, b) => b.count - a.count || a.field.localeCompare(b.field));
}

/**
 * The exact strings a reference's stored calibre text can equal and still be
 * counted for this calibre by anyone: its name and every alias, each in both
 * spellings of "calibre". Used to ask the search index which brands hold such
 * references. Brand scope on an alias is ignored here — it only widens the net,
 * and the scan applies the real rules.
 */
export function calibreTextLookupNames(
  entry: Pick<CustomCalibreEntry, 'name' | 'alsoKnownAs' | 'aliasDetails'>,
): string[] {
  const raw = [
    entry.name ?? '',
    ...(entry.alsoKnownAs ?? []),
    ...(entry.aliasDetails ?? []).map((a) => a?.name ?? ''),
  ];
  return Array.from(new Set(raw.flatMap((name) => (name ? spellingCandidates(name) : []))));
}

/**
 * The brands the DEFAULT scope scans — every brand a reference of this calibre
 * can plausibly live in without reading the whole catalogue:
 *
 *  - brands that already have a reference LINKED to it by id;
 *  - the calibre's own brand;
 *  - brands its brand-scoped aliases are scoped to;
 *  - brands with a reference whose text exactly matches its name or an alias
 *    (from the search index).
 *
 * The first rule alone was the original scope, and it is empty for any calibre
 * nobody has linked yet — which is exactly the calibre that most needs the tool.
 * Together these cover what the Refs column counts. "All brands" remains the
 * wider net for spellings an exact lookup cannot see.
 */
export function reapplyScopeBrandIds(input: {
  linkedBrandIds: readonly string[];
  entry: Pick<CustomCalibreEntry, 'brandId' | 'aliasDetails'>;
  textMatchBrandIds: readonly string[];
}): string[] {
  const ids = new Set<string>();
  const add = (id: unknown) => {
    const trimmed = typeof id === 'string' ? id.trim() : '';
    if (trimmed) ids.add(trimmed);
  };
  input.linkedBrandIds.forEach(add);
  add(input.entry.brandId);
  for (const alias of input.entry.aliasDetails ?? []) add(alias?.brandId);
  input.textMatchBrandIds.forEach(add);
  return Array.from(ids).sort();
}

/** "Caliber"/"Calibre" spellings of a name, as the server's count query tries them. */
function spellingCandidates(name: string): string[] {
  const trimmed = name.trim();
  if (!trimmed) return [];
  const swap = (from: RegExp, to: string) =>
    trimmed.replace(from, (word) => (word[0] === word[0].toUpperCase() ? to[0].toUpperCase() : to[0]) + to.slice(1) + (/s$/i.test(word) ? 's' : ''));
  return Array.from(
    new Set([trimmed, swap(/\bcalibers?\b/gi, 'calibre'), swap(/\bcalibres?\b/gi, 'caliber')]),
  );
}

/**
 * Would the server count this reference for the calibre BY TEXT today?
 *
 * The count's text match is an exact, case-sensitive equality on the stored
 * `calibre` string against the calibre's name and aliases (either spelling of
 * "calibre"), with a brand-scoped alias honoured only in its own brand. That is
 * stricter than the matcher this tool uses — which is why a reference can be
 * matched here yet missing from the Refs column until it is applied.
 */
export function isCountedByTextNow(
  ref: Pick<WatchReference, 'calibre' | 'brandId'>,
  entry: Pick<CustomCalibreEntry, 'name' | 'alsoKnownAs' | 'aliasDetails'>,
): boolean {
  const stored = typeof ref.calibre === 'string' ? ref.calibre : '';
  if (!stored) return false;
  const names: string[] = [entry.name ?? ''];
  const scoped = new Set<string>();
  for (const detail of entry.aliasDetails ?? []) {
    if (!detail?.name) continue;
    if (detail.brandId) {
      if (detail.brandId === ref.brandId) names.push(detail.name);
      scoped.add(detail.name);
    } else {
      names.push(detail.name);
    }
  }
  // `alsoKnownAs` entries with no detail row are universal.
  for (const alias of entry.alsoKnownAs ?? []) {
    if (alias && !scoped.has(alias)) names.push(alias);
  }
  return names.some((name) => spellingCandidates(name).includes(stored));
}

export interface RefsColumnForecast {
  /** The Refs column's value now (the stored count). */
  current: number;
  /** What it should read once the selection has been applied and recounted. */
  after: number;
}

/**
 * What applying the selection does to the calibre's Refs column.
 *
 * Only parents and standalone references are ever counted, so variants do not
 * move it. A selected reference adds one when it is not counted today and will
 * be afterwards:
 *  - `unionSemantics` (the server stores the linked/unlinked split): counted
 *    today when linked by ID or matched by text; always counted once applied,
 *    because applying links it.
 *  - otherwise (text-only count): counted today only by text; counted
 *    afterwards only if Apply rewrites its calibre text (`rewritesText`).
 *
 * It is a forecast from the references this scan found, laid over the stored
 * count — so it is right when the stored count is, and the recount that follows
 * every Apply reports the real figure.
 */
export function forecastRefsColumn(
  selected: readonly ReapplyMatchedRef[],
  entry: Pick<CustomCalibreEntry, 'name' | 'alsoKnownAs' | 'aliasDetails' | 'referenceCount'>,
  options: { unionSemantics: boolean; rewritesText: boolean },
): RefsColumnForecast {
  const current = typeof entry.referenceCount === 'number' ? entry.referenceCount : 0;
  let gained = 0;
  for (const m of selected) {
    if (m.isVariant) continue;
    const byText = isCountedByTextNow(m.ref, entry);
    const countedNow = byText || (options.unionSemantics && m.source === 'link');
    const countedAfter = options.unionSemantics || options.rewritesText || byText;
    if (!countedNow && countedAfter) gained += 1;
  }
  return { current, after: current + gained };
}
